import * as React from 'react';
import { Check, FunnelX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/lib/use-debounce';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { countActiveFilters, type FilterDef, type FilterValues } from '@/components/data/filters';

export type { FilterDef, FilterValues } from '@/components/data/filters';

export interface FilterBarProps {
  filters: FilterDef[];
  values: FilterValues;
  onChange: (values: FilterValues) => void;
  className?: string;
}

/**
 * Generic filter row: enum multi-selects (popover + checklist), debounced text inputs and boolean
 * switches. Values map 1:1 to API query params, so `api.get('/positions', { query: values })` works.
 */
export function FilterBar({ filters, values, onChange, className }: FilterBarProps) {
  const set = (key: string, value: FilterValues[string]) => {
    const next = { ...values };
    if (
      value === undefined ||
      value === '' ||
      value === false ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };
  const active = countActiveFilters(values);

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)} role="group" aria-label="Filters">
      {filters.map((f) => {
        switch (f.type) {
          case 'enum':
            return (
              <EnumFilter
                key={f.key}
                def={f}
                value={toArray(values[f.key])}
                onChange={(v) => set(f.key, v)}
              />
            );
          case 'text':
            return (
              <TextFilter
                key={f.key}
                def={f}
                value={typeof values[f.key] === 'string' ? (values[f.key] as string) : ''}
                onChange={(v) => set(f.key, v)}
              />
            );
          case 'boolean':
            return (
              <div key={f.key} className="flex h-9 items-center gap-2 rounded-md border border-dashed px-3">
                <Switch
                  id={`filter-${f.key}`}
                  checked={values[f.key] === true}
                  onCheckedChange={(v) => set(f.key, v)}
                />
                <Label htmlFor={`filter-${f.key}`} className="cursor-pointer text-sm font-normal">
                  {f.label}
                </Label>
              </div>
            );
          default:
            return null;
        }
      })}
      {active > 0 ? (
        <Button variant="ghost" size="sm" onClick={() => onChange({})} aria-label="Clear all filters">
          <FunnelX aria-hidden="true" /> Clear
        </Button>
      ) : null}
    </div>
  );
}

function toArray(v: FilterValues[string]): string[] {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v) return [v];
  return [];
}

function EnumFilter({
  def,
  value,
  onChange,
}: {
  def: Extract<FilterDef, { type: 'enum' }>;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const selected = new Set(value);
  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(def.options.filter((o) => next.has(o.value)).map((o) => o.value));
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-dashed" aria-label={`${def.label} filter`}>
          {def.label}
          {selected.size > 0 ? (
            <>
              <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
              {selected.size <= 2 ? (
                def.options
                  .filter((o) => selected.has(o.value))
                  .map((o) => (
                    <Badge key={o.value} variant="secondary" className="rounded-sm px-1 font-normal">
                      {o.label}
                    </Badge>
                  ))
              ) : (
                <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                  {selected.size} selected
                </Badge>
              )}
            </>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder={def.placeholder ?? def.label} />
          <CommandList>
            <CommandEmpty>No options.</CommandEmpty>
            <CommandGroup>
              {def.options.map((o) => {
                const isSelected = selected.has(o.value);
                return (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => toggle(o.value)}
                    aria-selected={isSelected}
                    role="option"
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                      )}
                      aria-hidden="true"
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    {o.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selected.size > 0 ? (
              <CommandGroup>
                <CommandItem
                  value="__clear"
                  onSelect={() => onChange([])}
                  className="justify-center text-center"
                >
                  Clear
                </CommandItem>
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TextFilter({
  def,
  value,
  onChange,
}: {
  def: Extract<FilterDef, { type: 'text' }>;
  value: string;
  onChange: (v: string) => void;
}) {
  const [local, setLocal] = React.useState(value);
  const debounced = useDebouncedValue(local, 300);
  const lastSent = React.useRef(value);

  React.useEffect(() => {
    if (debounced !== lastSent.current) {
      lastSent.current = debounced;
      onChange(debounced);
    }
  }, [debounced, onChange]);

  // External reset (e.g. "Clear") -> reflect in the input.
  React.useEffect(() => {
    if (value !== lastSent.current) {
      lastSent.current = value;
      setLocal(value);
    }
  }, [value]);

  return (
    <Input
      type="search"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      placeholder={def.placeholder ?? def.label}
      aria-label={def.label}
      className="h-9 w-full sm:w-56"
    />
  );
}
